import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import type { ApartmentsDefaultsResponse } from '../types/apartments';
import { Navbar } from './Navbar';
import { datadogRum } from '@datadog/browser-rum';

export const Apartments: React.FC = () => {
  const [data, setData] = useState<ApartmentsDefaultsResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    datadogRum.startView({ name: 'Apartments List' });
    
    let isMounted = true;
    (async () => {
      try {
        const res = await apiService.getApartments();
        if (isMounted) setData(res);
      } catch (e) {
        if (isMounted) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('apartments.loading')}</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  const apartments = Object.entries(data || {}).map(([slug, item]) => {
    const name = item.name || slug;
    const address = item.apartment?.address || '-';
    const tenants = (item.tenants || []).map(t => t.name).join(' & ');
    const rent = item.apartment?.rent || 0;
    const charges = item.apartment?.charges || 0;
    return { slug, name, address, tenants, rent, charges };
  });

  const handleCardClick = (slug: string) => {
    navigate(`/apartments/${slug}`);
  };

  // Generate a consistent color based on the slug
  const getCardGradient = (slug: string) => {
    const gradients = [
      'from-blue-500 to-purple-600',
      'from-purple-500 to-pink-600',
      'from-green-500 to-teal-600',
      'from-orange-500 to-red-600',
      'from-indigo-500 to-blue-600',
      'from-pink-500 to-rose-600',
    ];
    const hash = slug.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{t('apartments.title')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('apartments.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apartments.map(apt => (
              <div
                key={apt.slug}
                onClick={() => handleCardClick(apt.slug)}
                className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 flex flex-col"
              >
                <div className={`h-48 bg-gradient-to-br ${getCardGradient(apt.slug)} flex items-center justify-center`}>
                  <div className="text-white text-center p-6">
                    <svg className="w-20 h-20 mx-auto mb-2 opacity-80" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                    <h3 className="text-xl font-bold">{apt.name}</h3>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="space-y-3 flex-grow">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{t('apartments.address')}</p>
                      <p className="text-sm text-gray-900 mt-1">{apt.address}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{t('apartments.tenants')}</p>
                      <p className="text-sm text-gray-900 mt-1">{apt.tenants || t('apartments.noTenants')}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500">{t('apartments.rent')}</p>
                      <p className="text-lg font-semibold text-gray-900">{apt.rent}€</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('apartments.charges')}</p>
                      <p className="text-lg font-semibold text-gray-900">{apt.charges}€</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 